import type { CSSProperties } from 'react';
import { colors, spacing, typography, borderRadius, shadows } from '../../styles/tokens';

export const styles: Record<string, CSSProperties> = {
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: '20px 30px',
    maxWidth: '1000px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
    // Light green gradient surface, persists for the whole chat session.
    background: 'linear-gradient(180deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)',
    minHeight: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.md,
    marginBottom: spacing.lg,
    paddingBottom: '20px',
    borderBottom: `2px solid ${colors.brandGold}`,
  },
  headerIcon: {
    width: '56px',
    height: '56px',
    borderRadius: borderRadius.lg,
    background: `linear-gradient(135deg, ${colors.brandBlue} 0%, ${colors.brandBlueDark} 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.white,
    boxShadow: '0 4px 12px rgba(0,119,182,0.3)',
  },
  headerContent: {
    flex: 1,
  },
  title: {
    fontSize: typography.fontSize.xl,
    fontWeight: '700',
    color: '#1a1a1a',
    margin: 0,
  },
  subtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.brandGold,
    margin: '4px 0 0 0',
    fontWeight: '600',
  },
  headerActions: {
    display: 'flex',
    gap: spacing.sm,
  },
  audioToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    padding: '10px 16px',
    borderRadius: borderRadius.md,
    border: '1px solid #ddd',
    color: '#333',
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    background: colors.grayLight,
  },
  audioLabel: {
    fontSize: '13px',
    color: '#333',
  },
  questionsToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    padding: '10px 16px',
    borderRadius: borderRadius.md,
    border: '1px solid #ddd',
    color: '#333',
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    background: colors.grayLight,
  },

  // Messages
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.md,
    // Transparent so the parent container's green gradient shows through
    // for the entire chat session, not just the empty state.
    background: 'transparent',
    borderRadius: borderRadius.lg,
    margin: '0 -10px',
    paddingLeft: '10px',
    paddingRight: '10px',
  },
  messageWrapper: {
    display: 'flex',
    width: '100%',
  },
  messageBubble: {
    maxWidth: '85%',
    padding: spacing.md,
    borderRadius: borderRadius.xl,
    position: 'relative',
  },
  userBubble: {
    background: `linear-gradient(135deg, ${colors.brandBlue} 0%, ${colors.brandBlueDark} 100%)`,
    borderBottomRightRadius: borderRadius.sm,
    color: colors.white,
  },
  assistantBubble: {
    background: colors.bgAssistantBubble,
    borderBottomLeftRadius: borderRadius.sm,
    border: '1px solid #e0e0e0',
  },
  aiAvatar: {
    position: 'absolute',
    top: '-10px',
    left: '-10px',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${colors.brandGold} 0%, #B8962E 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.white,
    border: `2px solid ${colors.white}`,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  },
  messageText: {
    fontSize: '15px',
    lineHeight: '1.6',
    margin: 0,
    whiteSpace: 'pre-wrap',
  },
  messageTextUser: {
    color: colors.white,
  },
  messageTextAssistant: {
    color: '#1a1a1a',
  },

  // Vehicle Cards
  vehicleGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px',
    marginTop: spacing.md,
  },
  vehicleCard: {
    background: colors.white,
    border: '1px solid #e0e0e0',
    borderRadius: borderRadius.lg,
    padding: spacing.md,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
    boxShadow: shadows.card,
  },
  vehicleInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  vehicleYear: {
    fontSize: typography.fontSize.xs,
    color: colors.brandBlue,
    fontWeight: '600',
  },
  vehicleModel: {
    fontSize: typography.fontSize.base,
    fontWeight: '700',
    color: '#1a1a1a',
  },
  vehicleTrim: {
    fontSize: typography.fontSize.xs,
    color: '#666',
  },
  vehicleDetails: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: spacing.sm,
  },
  vehicleColor: {
    fontSize: typography.fontSize.xs,
    color: '#888',
  },
  vehiclePrice: {
    fontSize: typography.fontSize.base,
    fontWeight: '700',
    color: colors.brandGold,
  },
  viewButton: {
    fontSize: '13px',
    color: colors.brandBlue,
    fontWeight: '600',
    marginTop: spacing.sm,
  },

  // Loading
  loadingDots: {
    display: 'flex',
    gap: '6px',
    padding: '8px 0',
  },
  dot: {
    fontSize: typography.fontSize.xs,
    color: colors.brandBlue,
    animation: 'pulse 1s infinite',
  },

  // Suggestions
  suggestionsContainer: {
    padding: '20px',
    background: '#f8f9fa',
    borderRadius: borderRadius.lg,
    border: '1px solid #e0e0e0',
  },
  suggestionsLabel: {
    fontSize: '15px',
    color: '#1a1a1a',
    margin: '0 0 16px 0',
    fontWeight: '700',
  },
  suggestionsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
  },
  suggestionButton: {
    background: colors.white,
    border: `1px solid ${colors.brandBlue}`,
    borderRadius: '25px',
    padding: '12px 20px',
    color: colors.brandBlue,
    fontSize: typography.fontSize.sm,
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: shadows.sm,
  },

  // Input
  inputContainer: {
    display: 'flex',
    gap: '12px',
    padding: '20px 0',
    borderTop: '1px solid #e0e0e0',
    background: colors.white,
  },
  input: {
    flex: 1,
    padding: '16px 20px',
    background: colors.grayLight,
    border: '1px solid #ddd',
    borderRadius: borderRadius.lg,
    color: '#1a1a1a',
    fontSize: typography.fontSize.base,
    outline: 'none',
  },
  sendButton: {
    width: '56px',
    height: '56px',
    borderRadius: borderRadius.lg,
    background: `linear-gradient(135deg, ${colors.brandBlue} 0%, ${colors.brandBlueDark} 100%)`,
    border: 'none',
    color: colors.white,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 12px rgba(0,119,182,0.3)',
  },
  micButton: {
    width: '56px',
    height: '56px',
    borderRadius: borderRadius.lg,
    background: colors.grayLight,
    border: '1px solid #ddd',
    color: '#333',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
  },
  micButtonActive: {
    background: '#fee2e2',
    borderColor: colors.error,
    color: colors.error,
  },

  // Start Over
  startOverContainer: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: spacing.md,
  },
  startOverButton: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    background: colors.white,
    border: `2px solid ${colors.brandGold}`,
    borderRadius: borderRadius.md,
    padding: '12px 24px',
    color: colors.brandGold,
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  // Objection Panel
  objectionPanel: {
    background: '#f8f9fa',
    border: '1px solid #e0e0e0',
    borderRadius: borderRadius.xl,
    padding: '20px',
    marginBottom: spacing.md,
  },
  objectionHeader: {
    marginBottom: spacing.md,
  },
  objectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '700',
    color: '#1a1a1a',
    margin: '0 0 4px 0',
  },
  objectionSubtitle: {
    fontSize: typography.fontSize.sm,
    color: '#666',
    margin: 0,
  },
  objectionCategories: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: spacing.md,
  },
  objectionCategory: {
    background: colors.white,
    border: '1px solid #e0e0e0',
    borderRadius: borderRadius.lg,
    padding: spacing.md,
  },
  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: '12px',
  },
  categoryIcon: {
    fontSize: '20px',
  },
  categoryLabel: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: '#1a1a1a',
  },
  categoryPrompts: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
  },
  objectionPromptButton: {
    background: '#f0f7fa',
    border: `1px solid ${colors.brandBlue}`,
    borderRadius: borderRadius.md,
    padding: '10px 14px',
    color: colors.brandBlue,
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textAlign: 'left',
  },
  closePanelButton: {
    display: 'block',
    margin: '16px auto 0',
    padding: '10px 24px',
    background: colors.white,
    border: '1px solid #ddd',
    borderRadius: borderRadius.md,
    color: '#666',
    fontSize: typography.fontSize.sm,
    fontWeight: '500',
    cursor: 'pointer',
  },

  // Followups
  followupsContainer: {
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid #e0e0e0',
  },
  followupsLabel: {
    fontSize: typography.fontSize.xs,
    color: '#666',
    marginBottom: spacing.sm,
  },
  followupsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  followupButton: {
    background: '#f0f7fa',
    border: `1px solid ${colors.brandBlue}`,
    borderRadius: borderRadius.md,
    padding: '8px 12px',
    color: colors.brandBlue,
    fontSize: typography.fontSize.xs,
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },

  // Dealer Info
  dealerInfoContainer: {
    marginTop: spacing.md,
    borderRadius: borderRadius.lg,
    overflow: 'hidden',
    border: '1px solid #e0e0e0',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  dealerInfoImage: {
    width: '100%',
    height: 'auto',
    display: 'block',
  },
};

export default styles;
